import styled from "@emotion/styled";
import { useEffect, useState } from "react";

import {
  MDSBadge,
  MDSBadgeVariant,
  MDSButton,
  MDSButtonSize,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSCard,
  MDSCardVariant,
  MDSIcon,
  MDSIconKind,
  MDSSkeleton,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { AgentFilterSearchField } from "@/pages/agent/components/agent-filter-search-field";
import {
  getMemoryView,
  type MemoryEntity,
  type MemoryManifest,
  type MemoryOverview,
  type MemoryView,
  type MemoryViewResults,
} from "@/pages/agent/memory/memory-api";

const HERO_TYPE_KEYS = ["customer", "pain-point", "persona", "feature-request"];
const PAGE_SIZE = 50;
type MemoryViewLayout = "cards" | "quotes" | "ranked-list" | "table" | "timeline";

export const MemoryDashboard = ({
  overview,
  manifest,
  rootView,
  viewResults,
  selectedViewId,
  isSyncing,
  onSelectView,
  onSelectEntity,
  onBack,
  onEdit,
  onSync,
}: {
  overview: MemoryOverview;
  manifest: MemoryManifest;
  rootView: MemoryViewResults | null;
  viewResults: Record<string, MemoryViewResults>;
  selectedViewId: string | null;
  isSyncing: boolean;
  onSelectView: (viewId: string | null) => void;
  onSelectEntity: (entityId: string) => void;
  onBack: () => void;
  onEdit: () => void;
  onSync: () => void;
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [pageOffset, setPageOffset] = useState(0);
  const [selectedResults, setSelectedResults] = useState<MemoryViewResults | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const childViews = rootView?.children ?? [];
  const isCustomerIntelligence = manifest.key === "customer-intelligence";
  const isTrackingActive = manifest.status === "active";
  const metricTypeKeys = isCustomerIntelligence
    ? HERO_TYPE_KEYS
    : childViews
        .map(view => view.query.type_key)
        .filter((key): key is string => typeof key === "string")
        .filter((key, index, keys) => keys.indexOf(key) === index)
        .slice(0, 4);

  useEffect(() => {
    if (!selectedViewId) {
      setSelectedResults(null);
      setViewError(null);
      return;
    }
    let isCurrent = true;
    setSelectedResults(null);
    const timeoutId = window.setTimeout(
      () => {
        getMemoryView({
          viewId: selectedViewId,
          query: searchQuery.trim() || undefined,
          limit: PAGE_SIZE,
          offset: pageOffset,
        })
          .then(results => {
            if (isCurrent) {
              setSelectedResults(results);
              setViewError(null);
            }
          })
          .catch(error => {
            if (isCurrent) {
              setViewError(error instanceof Error ? error.message : "Unable to load this view");
            }
          });
      },
      searchQuery ? 200 : 0
    );
    return () => {
      isCurrent = false;
      window.clearTimeout(timeoutId);
    };
  }, [manifest.updated_at, pageOffset, searchQuery, selectedViewId]);

  const selectView = (viewId: string | null) => {
    setSearchQuery("");
    setPageOffset(0);
    onSelectView(viewId);
  };

  if (selectedViewId) {
    return (
      <DashboardRoot>
        <ViewToolbar>
          <MDSButton
            size={MDSButtonSize.Small}
            variant={MDSButtonVariant.Transparent}
            onPress={() => selectView(null)}
          >
            <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.AngleLeft} />
            {manifest.display_name}
          </MDSButton>
          <AgentFilterSearchField
            ariaLabel={`Filter ${selectedResults?.view.display_name ?? "Memory view"}`}
            placeholder="Filter by name or summary"
            value={searchQuery}
            onChange={value => {
              setSearchQuery(value);
              setPageOffset(0);
            }}
          />
        </ViewToolbar>
        {selectedResults ? (
          <>
            <ViewHeader>
              <ViewTitleRow>
                <MDSText as="h1" variant={MDSTextVariant.HeaderXl}>
                  {selectedResults.view.display_name}
                </MDSText>
                <MDSBadge
                  label={`${selectedResults.total} item${selectedResults.total === 1 ? "" : "s"}`}
                  variant={MDSBadgeVariant.Default}
                />
              </ViewTitleRow>
              <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphS}>
                {selectedResults.view.description}
              </MDSText>
            </ViewHeader>
            {selectedResults.children.length > 0 ? (
              <NestedViewGrid aria-label={`${selectedResults.view.display_name} views`}>
                {selectedResults.children.map(child => (
                  <NestedViewButton
                    key={child.id}
                    type="button"
                    onClick={() => selectView(child.id)}
                  >
                    <FullItemCopy>
                      <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.SemiBold}>
                        {child.display_name}
                      </MDSText>
                      <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
                        {child.description}
                      </MDSText>
                    </FullItemCopy>
                    <MDSIcon kind={MDSIconKind.AngleRight} size={12} />
                  </NestedViewButton>
                ))}
              </NestedViewGrid>
            ) : null}
            {selectedResults.items.length > 0 ? (
              <FullItemList $layout={resolveViewLayout(selectedResults.view)}>
                {selectedResults.items.map((item, index) => (
                  <MemoryItemRow
                    key={item.id}
                    index={selectedResults.offset + index}
                    item={item}
                    layout={resolveViewLayout(selectedResults.view)}
                    onSelectEntity={onSelectEntity}
                  />
                ))}
              </FullItemList>
            ) : selectedResults.children.length === 0 ? (
              <EmptyState>
                <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                  {searchQuery ? "No Memory items match this filter." : "Nothing found yet."}
                </MDSText>
              </EmptyState>
            ) : null}
            {selectedResults.total > PAGE_SIZE ? (
              <PaginationRow>
                <MDSButton
                  size={MDSButtonSize.Small}
                  variant={MDSButtonVariant.Chrome}
                  isDisabled={selectedResults.offset === 0}
                  onPress={() => setPageOffset(Math.max(0, pageOffset - PAGE_SIZE))}
                >
                  Previous
                </MDSButton>
                <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
                  {selectedResults.offset + 1}–
                  {Math.min(
                    selectedResults.offset + selectedResults.items.length,
                    selectedResults.total
                  )}{" "}
                  of {selectedResults.total}
                </MDSText>
                <MDSButton
                  size={MDSButtonSize.Small}
                  variant={MDSButtonVariant.Chrome}
                  isDisabled={
                    selectedResults.offset + selectedResults.items.length >= selectedResults.total
                  }
                  onPress={() => setPageOffset(pageOffset + PAGE_SIZE)}
                >
                  Next
                </MDSButton>
              </PaginationRow>
            ) : null}
          </>
        ) : viewError ? (
          <EmptyState>
            <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Danger}>
              {viewError}
            </MDSText>
          </EmptyState>
        ) : (
          <InitialViewLoading aria-label="Loading Memory view">
            <MDSSkeleton width="38%" height={28} />
            <MDSSkeleton width="100%" height={76} />
            <MDSSkeleton width="100%" height={76} />
          </InitialViewLoading>
        )}
      </DashboardRoot>
    );
  }

  return (
    <DashboardRoot>
      <RootToolbar>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Transparent}
          onPress={onBack}
        >
          <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.AngleLeft} />
          Memory
        </MDSButton>
        <MDSButton size={MDSButtonSize.Small} variant={MDSButtonVariant.Chrome} onPress={onEdit}>
          <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Sparkle} />
          Change with Agent
        </MDSButton>
      </RootToolbar>
      <HeroHeader>
        <HeroCopy>
          <StatusRow>
            <LiveDot $isActive={isTrackingActive} aria-hidden="true" />
            <MDSText
              color={isTrackingActive ? MDSTextColor.Positive : MDSTextColor.Secondary}
              variant={MDSTextVariant.TextXs}
            >
              {isTrackingActive ? "Kept current from new notes" : "Tracking paused"}
            </MDSText>
          </StatusRow>
          <MDSText as="h1" variant={MDSTextVariant.HeaderXl}>
            {manifest.display_name}
          </MDSText>
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphSm}>
            {manifest.description}
          </MDSText>
        </HeroCopy>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Chrome}
          isLoading={isSyncing}
          onPress={onSync}
        >
          <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Refresh} />
          {isTrackingActive ? "Sync now" : "Resume tracking"}
        </MDSButton>
      </HeroHeader>

      <MetricGrid>
        {metricTypeKeys.map(typeKey => {
          const type = overview.types.find(item => item.key === typeKey);
          const view = childViews.find(item => item.query.type_key === typeKey);
          return (
            <MetricCard
              key={typeKey}
              variant={MDSCardVariant.Chrome}
              onPress={view ? () => selectView(view.id) : undefined}
            >
              <MetricIcon>
                <MDSIcon kind={iconForType(typeKey)} size={16} />
              </MetricIcon>
              <MDSText variant={MDSTextVariant.HeaderL}>
                {(type?.entity_count ?? 0).toLocaleString()}
              </MDSText>
              <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
                {type?.plural_display_name ?? humanizeKey(typeKey)}
              </MDSText>
            </MetricCard>
          );
        })}
      </MetricGrid>

      <Section>
        <SectionHeading>
          <div>
            <MDSText as="h2" variant={MDSTextVariant.HeaderM}>
              {isCustomerIntelligence ? "What customers are telling you" : "Connected memory"}
            </MDSText>
            <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
              Browse the views Mem Agent compiled and keeps current from your notes.
            </MDSText>
          </div>
        </SectionHeading>
        <SignalGrid>
          {childViews
            .filter(view => !isCustomerIntelligence || view.key !== "customers")
            .map(view => (
              <SignalCard key={view.id}>
                <SignalCardHeader>
                  <div>
                    <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.SemiBold}>
                      {view.display_name}
                    </MDSText>
                    <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
                      {view.description}
                    </MDSText>
                  </div>
                  <MDSButton
                    size={MDSButtonSize.Small}
                    variant={MDSButtonVariant.Transparent}
                    onPress={() => selectView(view.id)}
                  >
                    View all
                    <MDSIcon slot={MDSButtonSlotName.RightIcon} kind={MDSIconKind.AngleRight} />
                  </MDSButton>
                </SignalCardHeader>
                <PreviewItems results={viewResults[view.id]} onSelectEntity={onSelectEntity} />
              </SignalCard>
            ))}
          {!rootView ? (
            <>
              <SignalSkeleton />
              <SignalSkeleton />
            </>
          ) : null}
        </SignalGrid>
      </Section>

      <FooterText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
        View: {manifest.display_name} · User corrections are preserved across future syncs.
      </FooterText>
    </DashboardRoot>
  );
};

const PreviewItems = ({
  results,
  onSelectEntity,
}: {
  results: MemoryViewResults | undefined;
  onSelectEntity: (entityId: string) => void;
}) => {
  if (!results) {
    return (
      <PreviewList aria-label="Loading Memory view">
        <MDSSkeleton width="72%" height={14} />
        <MDSSkeleton width="58%" height={14} />
        <MDSSkeleton width="65%" height={14} />
      </PreviewList>
    );
  }
  if (results.items.length === 0) {
    return (
      <PreviewList>
        <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
          Nothing found yet
        </MDSText>
      </PreviewList>
    );
  }
  const layout = resolveViewLayout(results.view);

  return (
    <PreviewList>
      {results.items.slice(0, 4).map((item, index) => (
        <PreviewButton key={item.id} type="button" onClick={() => onSelectEntity(item.id)}>
          {layout === "ranked-list" ? (
            <Rank>{index + 1}</Rank>
          ) : layout === "timeline" ? (
            <TimelineDate dateTime={item.last_seen_at}>
              {formatTimelineDate(item.last_seen_at)}
            </TimelineDate>
          ) : (
            <PreviewTypeIcon>
              <MDSIcon kind={iconForType(item.type_key)} size={12} />
            </PreviewTypeIcon>
          )}
          <PreviewCopy>
            <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.SemiBold}>
              {item.display_name}
            </MDSText>
            <MDSText
              color={MDSTextColor.Tertiary}
              variant={MDSTextVariant.TextXs}
              numberOfLines={1}
            >
              {formatEntityContext(item)}
            </MDSText>
          </PreviewCopy>
          <MDSIcon kind={MDSIconKind.AngleRight} size={12} />
        </PreviewButton>
      ))}
    </PreviewList>
  );
};

const MemoryItemRow = ({
  index,
  item,
  layout,
  onSelectEntity,
}: {
  index: number;
  item: MemoryViewResults["items"][number];
  layout: MemoryViewLayout;
  onSelectEntity: (entityId: string) => void;
}) => (
  <FullItemButton $layout={layout} type="button" onClick={() => onSelectEntity(item.id)}>
    {layout === "ranked-list" ? (
      <LargeRank>{index + 1}</LargeRank>
    ) : layout === "timeline" ? (
      <LargeTimelineDate dateTime={item.last_seen_at}>
        {formatTimelineDate(item.last_seen_at)}
      </LargeTimelineDate>
    ) : (
      <TypeIcon>
        <MDSIcon
          kind={layout === "quotes" ? MDSIconKind.Quote : iconForType(item.type_key)}
          size={15}
        />
      </TypeIcon>
    )}
    <FullItemCopy>
      <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.SemiBold}>
        {item.display_name}
      </MDSText>
      {item.summary ? (
        <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS} numberOfLines={2}>
          {item.summary}
        </MDSText>
      ) : null}
    </FullItemCopy>
    <ItemStats>
      <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
        {formatEntityContext(item)}
      </MDSText>
      <MDSIcon kind={MDSIconKind.AngleRight} size={12} />
    </ItemStats>
  </FullItemButton>
);

const resolveViewLayout = (view: MemoryView): MemoryViewLayout => {
  const layout = view.presentation.layout;
  if (
    layout === "cards" ||
    layout === "quotes" ||
    layout === "ranked-list" ||
    layout === "table" ||
    layout === "timeline"
  ) {
    return layout;
  }

  if (view.kind === "timeline") return "timeline";
  if (view.kind === "ranked") return "ranked-list";
  return "table";
};

const formatEntityContext = (item: MemoryEntity): string => {
  if (item.related_entity_type_counts.length > 0) {
    return item.related_entity_type_counts
      .slice(0, 3)
      .map(relatedType => {
        const label =
          relatedType.count === 1
            ? relatedType.type_display_name
            : relatedType.type_plural_display_name;
        return `${relatedType.count} ${label}`;
      })
      .join(" · ");
  }

  if (item.related_entity_count > 0) {
    return `${item.related_entity_count} related item${item.related_entity_count === 1 ? "" : "s"}`;
  }

  return `${item.evidence_count} source${item.evidence_count === 1 ? "" : "s"}`;
};

const formatTimelineDate = (value: string): string =>
  new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" }).format(new Date(value));

const iconForType = (typeKey: string): MDSIconKind => {
  switch (typeKey) {
    case "customer":
      return MDSIconKind.User;
    case "pain-point":
    case "complaint":
      return MDSIconKind.Warning;
    case "persona":
      return MDSIconKind.Group;
    case "feature-request":
    case "improvement-request":
      return MDSIconKind.Sparkle;
    case "praise":
      return MDSIconKind.Heart;
    case "desire":
      return MDSIconKind.Star;
    default:
      return MDSIconKind.Cells;
  }
};

const humanizeKey = (key: string): string =>
  key.replaceAll("-", " ").replace(/^./, character => character.toUpperCase());

const DashboardRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[8]};
`;

const RootToolbar = styled.nav`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
`;

const InitialViewLoading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const PaginationRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: center;
`;

const HeroHeader = styled.header`
  align-items: flex-start;
  display: flex;
  gap: ${({ theme }) => theme.spacing[6]};
  justify-content: space-between;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  max-width: 680px;
`;

const StatusRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const LiveDot = styled.span<{ $isActive: boolean }>`
  background: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.text.positive : theme.colors.text.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  height: 7px;
  width: 7px;
`;

const MetricGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[3]};
  grid-template-columns: repeat(4, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const MetricCard = styled(MDSCard)`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
  text-align: left;
`;

const MetricIcon = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.button.toggle.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  display: flex;
  height: 30px;
  justify-content: center;
  width: 30px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SectionHeading = styled.div`
  align-items: flex-end;
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  justify-content: space-between;

  > div {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[1]};
  }
`;

const SignalGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[4]};
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const SignalCard = styled.div`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const SignalCardHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[4]};

  > div {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[1]};
  }
`;

const SignalSkeleton = () => (
  <SignalCard>
    <SignalCardHeader>
      <MDSSkeleton width="44%" height={18} />
    </SignalCardHeader>
    <PreviewList>
      <MDSSkeleton width="72%" height={14} />
      <MDSSkeleton width="58%" height={14} />
      <MDSSkeleton width="65%" height={14} />
    </PreviewList>
  </SignalCard>
);

const PreviewList = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border.surface};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  min-height: 112px;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
`;

const PreviewButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  color: inherit;
  cursor: pointer;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  margin: 0 -${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]};
  text-align: left;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.background.list.hover};
    outline: none;
  }
`;

const Rank = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.button.toggle.default};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  color: ${({ theme }) => theme.colors.text.tertiary};
  display: flex;
  flex: 0 0 auto;
  font-size: 10px;
  height: 20px;
  justify-content: center;
  width: 20px;
`;

const PreviewTypeIcon = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.button.toggle.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  color: ${({ theme }) => theme.colors.text.tertiary};
  display: flex;
  flex: 0 0 auto;
  height: 24px;
  justify-content: center;
  width: 24px;
`;

const TimelineDate = styled.time`
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex: 0 0 44px;
  font-size: 11px;
  text-align: left;
`;

const PreviewCopy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`;

const TypeIcon = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.button.toggle.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  display: flex;
  flex: 0 0 auto;
  height: 30px;
  justify-content: center;
  width: 30px;
`;

const FooterText = styled(MDSText)`
  border-top: 1px solid ${({ theme }) => theme.colors.border.surface};
  padding-top: ${({ theme }) => theme.spacing[4]};
`;

const ViewToolbar = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
`;

const ViewHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ViewTitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const FullItemList = styled.div<{ $layout: MemoryViewLayout }>`
  border: ${({ $layout, theme }) =>
    $layout === "cards" ? "0" : `1px solid ${theme.colors.border.surface}`};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  display: ${({ $layout }) => ($layout === "cards" ? "grid" : "block")};
  gap: ${({ $layout, theme }) => ($layout === "cards" ? theme.spacing[3] : "0")};
  grid-template-columns: ${({ $layout }) =>
    $layout === "cards" ? "repeat(2, minmax(0, 1fr))" : "none"};
  overflow: hidden;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const NestedViewGrid = styled.nav`
  display: grid;
  gap: ${({ theme }) => theme.spacing[3]};
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const NestedViewButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  color: inherit;
  cursor: pointer;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  text-align: left;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.background.list.hover};
    outline: none;
  }
`;

const FullItemButton = styled.button<{ $layout: MemoryViewLayout }>`
  align-items: center;
  background: ${({ $layout, theme }) =>
    $layout === "cards" ? theme.colors.background.input.chrome.default : "transparent"};
  border: ${({ $layout, theme }) =>
    $layout === "cards" ? `1px solid ${theme.colors.border.surface}` : "0"};
  border-radius: ${({ $layout, theme }) => ($layout === "cards" ? theme.borderRadius.xlarge : "0")};
  color: inherit;
  cursor: pointer;
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[4]};
  text-align: left;
  width: 100%;

  & + & {
    border-top: ${({ $layout, theme }) =>
      $layout === "cards" ? undefined : `1px solid ${theme.colors.border.surface}`};
  }

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.background.list.hover};
    outline: none;
  }
`;

const LargeRank = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex: 0 0 30px;
  font-size: 18px;
  text-align: center;
`;

const LargeTimelineDate = styled.time`
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex: 0 0 52px;
  font-size: 12px;
  text-align: left;
`;

const FullItemCopy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const ItemStats = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const EmptyState = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  display: flex;
  justify-content: center;
  min-height: 180px;
  padding: ${({ theme }) => theme.spacing[6]};
`;
